import { ChangeEvent, FormEvent, useState } from 'react';
import { white } from 'config/colors';
import TextField from 'components/Form/TextField';
import { TextFieldContainer } from 'views/auth/Common';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { ICreateLiveClassFormValues } from 'types/liveClass';
import CircularProgress from '@mui/material/CircularProgress';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';

interface ICreateLiveClassFormProps {
  isCreating: boolean;
  newLiveClass: ICreateLiveClassFormValues;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  setNewLiveClass: (liveClass: ICreateLiveClassFormValues) => void;
}

const CreateLiveClassForm = ({
  handleSubmit,
  isCreating,
  newLiveClass,
  setNewLiveClass,
}: ICreateLiveClassFormProps) => {
  const [isDateTimePickerTouched, setIsDateTimePickerTouched] = useState(false);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewLiveClass({
      ...newLiveClass,
      [name]: value,
    });
  };

  const handleSetScheduledDateTime = (time: string | null) => {
    setIsDateTimePickerTouched(true);

    if (!time) return;
    setNewLiveClass({
      ...newLiveClass,
      scheduledDateTime: time,
    });
  };

  return (
    <div>
      <form className="d-flex flex-column" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="sessionTitle">Session title:</label>

          <div className="mt-1">
            <input
              name="sessionTitle"
              id="sessionTitle"
              required
              type="text"
              className="form-control mb-2 py-3"
              disabled
              value={newLiveClass.basicSessionId.title}
            />
          </div>
        </div>

        <div>
          <label htmlFor="sessionStatus">Session status:</label>

          <div className="mt-1">
            <input
              name="sessionStatus"
              id="sessionStatus"
              required
              type="text"
              className="form-control mb-2 py-3"
              disabled
              value={newLiveClass.basicSessionId.status}
            />
          </div>
        </div>

        <div className="w-100">
          <label htmlFor="title">Title:</label>

          <div className="mt-1">
            <input
              name="title"
              id="title"
              required
              type="text"
              className="form-control mb-2 py-3"
              value={newLiveClass.title}
              onChange={handleOnChange}
            />
          </div>
        </div>

        <div className="w-100">
          <label htmlFor="description">Description:</label>

          <div className="mt-1">
            <input
              name="description"
              id="description"
              type="text"
              className="form-control mb-2 py-3"
              value={newLiveClass.description}
              onChange={handleOnChange}
            />
          </div>
        </div>

        <div className="d-flex flex-row w-100 column-gap-3">
          <div className="w-50">
            <label htmlFor="instructorName">Instructor name:</label>

            <div className="mt-1">
              <input
                name="instructorName"
                id="instructorName"
                required
                type="text"
                className="form-control mb-2 py-3"
                value={newLiveClass.instructorName}
                onChange={handleOnChange}
              />
            </div>
          </div>

          <div className="w-50">
            <label htmlFor="link">Class link:</label>

            <div className="mt-1">
              <input
                name="link"
                id="link"
                required
                type="text"
                className="form-control mb-2 py-3"
                value={newLiveClass.link}
                onChange={handleOnChange}
              />
            </div>
          </div>
        </div>

        <div className="w-100">
          <TextFieldContainer className="w-100">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDateTimePicker
                label="Scheduled Date and Time"
                value={newLiveClass.scheduledDateTime}
                onChange={handleSetScheduledDateTime}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={
                      !!(isDateTimePickerTouched && newLiveClass.scheduledDateTime.length === 0)
                    }
                    helperText={
                      isDateTimePickerTouched && newLiveClass.scheduledDateTime.length === 0
                        ? 'Scheduled Date time is required'
                        : ''
                    }
                  />
                )}
              />
            </LocalizationProvider>
          </TextFieldContainer>
          <div className="mt-2 d-flex flex-column">
            <span>User suggested availablity time (In 24 hour format):</span>
            <span>{`${newLiveClass.basicSessionId.availability?.times.start} - ${newLiveClass.basicSessionId.availability?.times.end}`}</span>
          </div>
        </div>

        <div className="mt-4">
          <button type="submit" className="btn btn-primary btn-block">
            {isCreating ? (
              <CircularProgress sx={{ color: white }} size={24} thickness={8} />
            ) : (
              'Create class'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateLiveClassForm;
